import React, { useEffect, useState } from 'react'
import { CalendarPlus, Save, Trash2 } from 'lucide-react'
import * as api from '../api'
import { useHR } from '../../hr/HRContext'
import { can, clearPy, Confirm, dmy, EditModal, ErrorBox, invalidate, Loading, MONTHS, Panel, plabel, PyHeader, Stat, usePyMeta } from '../py'
import { Select } from '../../hr/components/Controls'

// Stephanet's Date Control (custom/pay15/DateControl.jsx), ported unchanged; holidays are the shared public_holidays.

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const FULL = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' }

export default function DateControl() {
  const { notify } = useHR()
  const [meta, reloadMeta] = usePyMeta()
  const [d, setD] = useState(null)
  const [f, setF] = useState(null)
  const [err, setErr] = useState('')
  const [add, setAdd] = useState(false)
  const [del, setDel] = useState(null)
  const [busy, setBusy] = useState(false)
  const load = (per) => api.dateControl(per).then(x => {
    setD(x); if (!per) setF({ month: x.control.month, year: x.control.year, start_day: x.control.start_day, work_week: x.control.work_week })
  }).catch(e => setErr(e.message))
  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // preview the calendar for the month being chosen
  useEffect(() => { if (f && d) load(`${f.year}-${String(f.month).padStart(2, '0')}`) }, [f?.month, f?.year]) // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorBox error={err} />
  if (!d || !f) return <Loading what="date control" />
  const edit = can(meta, 'py-date-control')
  const P = d.period
  const changed = f.month !== d.control.month || f.year !== d.control.year || f.start_day !== d.control.start_day ||
    DAYS.some(x => !!f.work_week[x] !== !!d.control.work_week[x])
  // a work-week change only shows after saving; preview the day types here
  const typeOf = x => (x.type === 'PH' ? 'PH' : f.work_week[x.weekday] ? 'WD' : 'RD')
  const counts = P.days.reduce((t, x) => ({ ...t, [typeOf(x)]: (t[typeOf(x)] || 0) + 1 }), {})
  const save = async () => {
    setBusy(true)
    try { const r = await api.saveDateControl(f); notify(r.message); clearPy(); invalidate(); await reloadMeta(); load() }
    catch (e) { notify(e.message, 'bad') } finally { setBusy(false) }
  }
  const years = Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 4 + i)
  return (
    <div className="ps py">
      <PyHeader title="Date Control" sub="Choose the payroll month — every site sheet, the engine and the reports move with it." meta={meta} period={d.active}>
        {edit && <button className="mds-run" disabled={busy || !changed} onClick={save}><Save size={14} /> Save & Make Active</button>}
      </PyHeader>
      <div className="hre-grid2">
        <Panel title="Payroll Period">
          <div className="rec-form-grid">
            <label className="rec-f"><span>Payroll month</span><Select title="Payroll month" disabled={!edit} value={f.month} onChange={e => setF({ ...f, month: Number(e.target.value) })}>
              {MONTHS.map((x, i) => <option key={x} value={i + 1}>{x}</option>)}</Select></label>
            <label className="rec-f"><span>Payroll year</span><Select title="Payroll year" disabled={!edit} value={f.year} onChange={e => setF({ ...f, year: Number(e.target.value) })}>
              {years.map(y => <option key={y}>{y}</option>)}</Select></label>
            <label className="rec-f s2"><span>Pay period starts on day</span><input type="number" min="1" max="28" disabled={!edit} value={f.start_day}
              onChange={e => setF({ ...f, start_day: Number(e.target.value) })} />
              <small className="muted">21 → 21st of last month to 20th of this month (your cycle). 1 → calendar month.</small></label>
          </div>
          <div className="py-stats">
            <Stat label="Payroll" value={plabel(`${f.year}-${String(f.month).padStart(2, '0')}`).toUpperCase()} />
            <Stat label="Period start" value={dmy(P.start)} /><Stat label="Period end" value={dmy(P.end)} />
            <Stat label="Days in period" value={P.days.length} /><Stat label="Scheduled working days" value={counts.WD || 0} tone="blue" />
            <Stat label="Rest days" value={counts.RD || 0} /><Stat label="Public holidays" value={counts.PH || 0} tone="pink" />
          </div>
        </Panel>
        <Panel title="Work Week" note="Y = working day">
          <div className="py-week">
            {DAYS.map(x => <label key={x} className={`py-day ${f.work_week[x] ? 'on' : ''}`}>
              <input type="checkbox" disabled={!edit} checked={!!f.work_week[x]} onChange={e => setF({ ...f, work_week: { ...f.work_week, [x]: e.target.checked } })} />
              <b>{FULL[x]}</b><span>{f.work_week[x] ? 'Working day' : 'Rest day'}</span></label>)}
          </div>
          <p className="muted small">WD = working day, RD = rest day, PH = public holiday (from the list below). Rest-day and holiday hours are paid at the premium rates in Setup.</p>
        </Panel>
      </div>
      <Panel title={`Calendar for this Period — ${P.text}`} note={changed ? 'preview — save to apply' : 'automatic'}>
        <div className="py-cal">
          {P.days.map(x => <div key={x.date} className={`py-cal-d t-${typeOf(x)}`} title={x.holiday || ''}>
            <small>Day {x.n} · {x.weekday}</small><b>{dmy(x.date).slice(0, 6)}</b><span>{typeOf(x)}</span>{x.holiday && <em>{x.holiday}</em>}</div>)}
        </div>
      </Panel>
      <Panel title="Public Holidays" note={`${d.holidays.length} holiday(s)`}>
        {edit && <div className="py-panel-actions"><button className="mdr-btn" onClick={() => setAdd(true)}><CalendarPlus size={14} /> Add Holiday</button></div>}
        <table className="ps-table py-simple">
          <thead><tr><th>Date</th><th>Holiday</th><th>Weekday</th><th>Note</th><th>Recurring</th>{edit && <th />}</tr></thead>
          <tbody>{d.holidays.map(h => <tr key={h.id} className={P.start <= h.date && h.date <= P.end ? 'py-hl' : ''}>
            <td>{dmy(h.date)}</td><td>{h.name}</td><td>{FULL[DAYS[(new Date(`${h.date}T00:00:00`).getDay() + 6) % 7]]}</td><td>{h.note && h.note !== 'All' ? h.note : ''}</td>
            <td>{h.recurring ? 'Yes' : ''}</td>
            {edit && <td className="r"><button className="icon-btn" title="Remove" onClick={() => setDel(h)}><Trash2 size={14} /></button></td>}</tr>)}</tbody>
        </table>
      </Panel>
      {add && <EditModal title="Add Public Holiday" wide={false} value={{}} onClose={() => setAdd(false)}
        fields={[{ k: 'date', label: 'Date', type: 'date', req: true }, { k: 'name', label: 'Holiday', req: true }, { k: 'note', label: 'Note (e.g. Declared by FG)', span: 2 },
          { k: 'recurring', label: 'Same date every year', type: 'check' }]}
        onSave={async v => { await api.saveHoliday(null, { holiday_date: v.date, name: v.name, description: v.note, recurring: !!v.recurring }); notify(`${v.name} added`); setAdd(false); invalidate(); load() }} />}
      {del && <Confirm title="Remove holiday" text={`Remove ${del.name} (${dmy(del.date)})?`} yes="Remove" danger onClose={() => setDel(null)}
        onYes={async () => { await api.deleteHoliday(del.id); notify(`${del.name} removed`); setDel(null); invalidate(); load() }} />}
    </div>
  )
}
